import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  TimeInputsRow,
  TimeLabel,
  TimeDropdownsContainer,
  TimeDropdown,
  TimeValue,
  TimeUnit,
} from '../DateRangeTimePicker.styles';
import CaretDown from '../icons/CaretDown';

const DIGITS_ONLY = /^\d+$/;

const TimeSelector = ({ label, hour, minute, ampm, onHourChange, onMinuteChange, onAmPmChange }) => {
  // NOTE(vlad|SPD-14091|2026-02-05): Local state prevents premature parent updates during typing
  // Actual update happens on blur or dropdown selection
  const [localHour, setLocalHour] = useState(hour);
  const [localMinute, setLocalMinute] = useState(minute);

  useEffect(() => {
    setLocalHour(hour);
  }, [hour]);

  useEffect(() => {
    setLocalMinute(minute);
  }, [minute]);

  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const val = i + 1;

    return String(val).padStart(2, '0');
  });

  const minuteOptions = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));

  const handleHourInput = (e) => {
    const value = e.target.value;
    if (value === '') {
      setLocalHour(value);

      return;
    }

    if (!DIGITS_ONLY.test(value)) {
      return;
    }

    const num = parseInt(value, 10);
    if (value.length === 1 || (num >= 1 && num <= 12)) {
      setLocalHour(value);
    }
  };

  const handleMinuteInput = (e) => {
    const value = e.target.value;
    if (value === '') {
      setLocalMinute(value);

      return;
    }

    if (!DIGITS_ONLY.test(value)) {
      return;
    }

    const num = parseInt(value, 10);
    if (value.length === 1 || (num >= 0 && num <= 59)) {
      setLocalMinute(value);
    }
  };

  const handleHourBlur = (e) => {
    const value = e.target.value;
    if (!value || parseInt(value, 10) < 1 || parseInt(value, 10) > 12) {
      onHourChange('12');
    } else {
      onHourChange(value);
    }
  };

  const handleMinuteBlur = (e) => {
    const value = e.target.value;
    if (!value || parseInt(value, 10) < 0 || parseInt(value, 10) > 59) {
      onMinuteChange('00');
    } else {
      onMinuteChange(value);
    }
  };

  return (
    <TimeInputsRow>
      <TimeLabel>{label}</TimeLabel>
      <TimeDropdownsContainer>
        <TimeDropdown $isAmPm={false}>
          <TimeValue type="text" value={localHour} onChange={handleHourInput} onBlur={handleHourBlur} maxLength={2} />
          <TimeUnit>hr</TimeUnit>
          <CaretDown size={12} />
          {/* NOTE(vlad|SPD-14824|2026-05-12): Pad to match zero-padded option values; otherwise
              the browser falls back to the first option ("01") regardless of the real hour. */}
          <select value={String(hour).padStart(2, '0')} onChange={(e) => onHourChange(e.target.value)}>
            {hourOptions.map((h) => (
              <option key={h} value={h}>
                {h}
              </option>
            ))}
          </select>
        </TimeDropdown>

        <TimeDropdown $isAmPm={false}>
          <TimeValue
            type="text"
            value={localMinute}
            onChange={handleMinuteInput}
            onBlur={handleMinuteBlur}
            maxLength={2}
          />
          <TimeUnit>min</TimeUnit>
          <CaretDown size={12} />
          <select value={minute} onChange={(e) => onMinuteChange(e.target.value)}>
            {minuteOptions.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
        </TimeDropdown>

        <TimeDropdown $isAmPm={true}>
          <TimeValue as="span">{ampm}</TimeValue>
          <CaretDown size={12} />
          <select value={ampm} onChange={(e) => onAmPmChange(e.target.value)}>
            <option value="AM">AM</option>
            <option value="PM">PM</option>
          </select>
        </TimeDropdown>
      </TimeDropdownsContainer>
    </TimeInputsRow>
  );
};

TimeSelector.propTypes = {
  label: PropTypes.string.isRequired,
  hour: PropTypes.string.isRequired,
  minute: PropTypes.string.isRequired,
  ampm: PropTypes.string.isRequired,
  onHourChange: PropTypes.func.isRequired,
  onMinuteChange: PropTypes.func.isRequired,
  onAmPmChange: PropTypes.func.isRequired,
};

export default TimeSelector;
